import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Building2, MapPin } from "lucide-react";
import { DataTable } from "@/components/common/DataTable";
import { StatusBadge } from "@/components/common/StatusBadge";
import { PageTransition } from "@/components/common/PageTransition";
import { usePageHeading } from "@/components/layout/AppLayout";
import { clinicApi } from "@/lib/api";
import { formatCurrency, formatNumber } from "@/lib/format";
import { TID } from "@/constants/testIds";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export default function Clinics() {
  usePageHeading("Clinic Management", "Browse, review and manage clinics");
  const navigate = useNavigate();
  const [clinics, setClinics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");

  useEffect(() => {
    (async () => {
      try {
        setClinics(await clinicApi.list());
      } catch {
        toast.error("Failed to load clinics");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const normalizedClinics = (clinics || []).map((c) => ({
    ...c,
    id: c.id ?? c.clinicId ?? c.clinic?.id,
    name: c.clinicName ?? c.name ?? c.clinic?.clinicName ?? "Clinic",
    owner: c.owner ?? c.doctorName ?? c.contactPerson ?? "Owner",
    city: c.city ?? "",
    plan: c.planName ?? c.plan ?? "Trial",
    mrr: c.mrr ?? 0,
    patients: c.patients ?? 0,
    status: (c.subscriptionStatus ?? c.status ?? "ACTIVE").toLowerCase(),
  }));

  const rows = statusFilter === "all" ? normalizedClinics : normalizedClinics.filter((c) => c.status === statusFilter);

  const columns = [
    {
      key: "name",
      header: "Clinic",
      sortable: true,
      searchable: true,
      render: (r) => (
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gold/15 font-heading text-sm font-semibold text-gold">
            {String(r.name).slice(0, 2).toUpperCase()}
          </div>
          <div className="min-w-0">
            <p className="truncate font-semibold">{r.name}</p>
            <p className="truncate text-xs text-muted-foreground">{r.owner}</p>
          </div>
        </div>
      ),
    },
    {
      key: "city",
      header: "Location",
      sortable: true,
      searchable: true,
      render: (r) => (
        <span className="inline-flex items-center gap-1.5 text-muted-foreground">
          <MapPin className="h-3.5 w-3.5" /> {r.city}
        </span>
      ),
    },
    { key: "plan", header: "Plan", sortable: true, render: (r) => <span className="font-medium">{r.plan}</span> },
    {
      key: "mrr",
      header: "MRR",
      sortable: true,
      render: (r) => <span className="font-semibold text-gold">{formatCurrency(r.mrr)}</span>,
    },
    { key: "patients", header: "Patients", sortable: true, render: (r) => formatNumber(r.patients) },
    { key: "status", header: "Status", sortable: true, render: (r) => <StatusBadge status={r.status} /> },
  ];

  const statusFilterEl = (
    <Select value={statusFilter} onValueChange={setStatusFilter}>
      <SelectTrigger className="h-10 w-[150px] border-border bg-secondary/30">
        <SelectValue placeholder="Status" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="all">All statuses</SelectItem>
        <SelectItem value="active">Active</SelectItem>
        <SelectItem value="pending">Pending</SelectItem>
        <SelectItem value="hold">On Hold</SelectItem>
        <SelectItem value="rejected">Rejected</SelectItem>
      </SelectContent>
    </Select>
  );

  return (
    <PageTransition className="space-y-6">
      <div data-testid={TID.clinicsPage} />
      <DataTable
        columns={columns}
        rows={rows}
        loading={loading}
        searchPlaceholder="Search clinics, owners, cities…"
        searchTestId={TID.clinicSearch}
        rowTestId={TID.clinicRow}
        filters={statusFilterEl}
        onRowClick={(r) => navigate(`/clinics/${r.id}`)}
        emptyTitle="No clinics found"
        emptyDescription="Try adjusting your search or filter."
      />
    </PageTransition>
  );
}
